/**
 * Country flag service.
 *
 * 0.36.269 — Loads the bulk country-flag bundle from
 * /api/iso/country-flags (~30-50 KB gzipped) on first use and caches
 * it in a module-level Map. All FlagIcon renders share the same
 * fetch promise so we never hit the endpoint more than once per
 * page load.
 *
 * Why module-level cache + hook (vs context):
 *   - Flags are immutable for the duration of a session — fetched
 *     once, never refreshed. Context would force prop drilling for
 *     no benefit.
 *   - Module cache survives unmount/remount of consumers, which
 *     happens often (route changes, layout shifts).
 *
 * API:
 *   - useCountryFlag(code) → string | null
 *     React hook. Returns the SVG markup string for the given
 *     alpha-2 country code (lowercased), or null while loading /
 *     when not found. Triggers a single shared fetch on first call.
 *
 *   - getCountryFlag(code) → string | null
 *     Sync helper for non-React callers (or if a caller has
 *     already awaited the load). Returns null until the bundle
 *     resolves.
 *
 *   - LANGUAGE_TO_COUNTRY: { [langCode]: countryCode }
 *     Mapping for the language switcher — language codes mapped
 *     to the country code whose flag should represent that
 *     language. Currently a small platform-specific map for our
 *     3 active languages. Languages without a mapping fall through
 *     to FlagIcon's globe fallback.
 */
import { useEffect, useState } from 'react';

// Module-level cache. Once the fetch resolves, this is the answer.
let _flagsMap = null;          // { am: '<svg…>', us: '<svg…>', … } once loaded
let _loadPromise = null;       // shared in-flight fetch promise
const _listeners = new Set();  // hooks subscribed for re-render once loaded

/**
 * Language → Country mapping for the language switcher.
 *
 * 0.36.269 — Hardcoded for the platform's current languages.
 * Admin can override per-language flag via the existing
 * "flag-{langCode}" uploaded asset slot (which still wins in
 * FlagIcon's priority chain).
 *
 * Future improvement: add a `country_code` column to the
 * Language model so admins can configure this in the admin UI
 * rather than via code. Tracked as a follow-up.
 */
export const LANGUAGE_TO_COUNTRY = {
  hy: 'am',  // Armenian → Armenia
  ru: 'ru',  // Russian → Russia (identical code, listed for clarity)
  en: 'gb',  // English → United Kingdom (could be 'us'; UK matches Henaket reference)
};

/**
 * Process a raw circle-flag SVG so it can safely render alongside
 * many other inline SVGs:
 *
 *  1. Namespace internal IDs. Every circle-flag SVG defines
 *     `<mask id="a">` and references it via `url(#a)`. With 243+
 *     flags inline on the page (phone picker dropdown), every one
 *     defines the same `id="a"` — colliding in the document ID
 *     namespace. Result: most flags render with the wrong mask
 *     coordinates or no mask at all. Fix: rewrite `id="a"` →
 *     `id="flag-{code}-a"` and `url(#a)` → `url(#flag-{code}-a)`.
 *
 *  2. Strip the upstream `width="512" height="512"` attributes.
 *     We want the consuming `<span>` wrapper to control size; the
 *     `viewBox="0 0 512 512"` (preserved) handles the math.
 *
 * Idempotent: runs once per SVG when the bundle loads, output
 * cached in `_flagsMap`. Per-render code can then inject the
 * processed SVG verbatim — no per-render rewriting.
 */
function _processSvg(svg, code) {
  // Step 1 — collect every `id="..."` declared inside this SVG.
  const ids = new Set();
  svg.replace(/\bid="([^"]+)"/g, (_, id) => { ids.add(id); return _; });
  // Step 2 — rewrite each declaration AND its url(#...) references.
  let result = svg;
  for (const id of ids) {
    const escaped = id.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const ns = `flag-${code}-${id}`;
    result = result.replace(new RegExp(`\\bid="${escaped}"`, 'g'), `id="${ns}"`);
    result = result.replace(new RegExp(`url\\(#${escaped}\\)`, 'g'), `url(#${ns})`);
  }
  // Step 3 — strip upstream width/height so the wrapper controls size.
  result = result.replace(/\s+width="\d+"/, '').replace(/\s+height="\d+"/, '');
  return result;
}

/** Kick off the fetch if it hasn't been started yet. Idempotent. */
function _ensureLoad() {
  if (_loadPromise) return _loadPromise;
  _loadPromise = fetch('/api/iso/country-flags', {
    credentials: 'include',
    cache: 'default',  // OK to use HTTP cache; the bundle is static for a session
  })
    .then(r => r.ok ? r.json() : Promise.reject(new Error(`flags HTTP ${r.status}`)))
    .then(data => {
      // 0.36.270 — Process every SVG once at load time. Namespacing
      // internal IDs is critical: without it, inline-rendering many
      // flags on the same page (phone picker dropdown) causes them
      // to collide on `id="a"` and render with the wrong masks. See
      // _processSvg() docstring.
      const processed = {};
      for (const [code, svg] of Object.entries(data || {})) {
        processed[code] = _processSvg(svg, code);
      }
      _flagsMap = processed;
      // Wake every hook subscriber so they re-render with the loaded flags.
      _listeners.forEach(fn => fn());
      return _flagsMap;
    })
    .catch(err => {
      console.warn('[countryFlags] load failed:', err);
      _flagsMap = {};
      _listeners.forEach(fn => fn());
      return _flagsMap;
    });
  return _loadPromise;
}

/**
 * Synchronous lookup. Returns null until the bundle loads.
 * Use the hook in React components; this helper is for non-React
 * callers.
 */
export function getCountryFlag(code) {
  if (!code) return null;
  if (_flagsMap == null) return null;
  return _flagsMap[code.toLowerCase()] || null;
}

/**
 * React hook for a single country flag by alpha-2 code.
 * Triggers the shared fetch on first use; re-renders the
 * consumer when the bundle resolves.
 */
export function useCountryFlag(code) {
  // Pull a snapshot. Will be null on first render, populated after fetch.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (_flagsMap != null) return;  // already loaded
    _ensureLoad();
    const onLoaded = () => setTick(t => t + 1);
    _listeners.add(onLoaded);
    return () => { _listeners.delete(onLoaded); };
  }, []);
  return getCountryFlag(code);
}
